/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountListInitialProperties,
  AccountListInterface,
  AccountListResponseInterFace,
  RequestAccountListInterFace,
} from '../../interface/account-list-penalty-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchStationsList = createAsyncThunk<
  AccountListResponseInterFace,
  RequestAccountListInterFace
>('account/fetchStationsListFilters', async (data: RequestAccountListInterFace, { dispatch }) => {
  try {
    const response = await penaltyManagementApi.fetchPenaltyManagementStation(
      removeEmptyKeys({ ...data }),
    );
    return response.data;
  } catch (error: any) {
    dispatch(
      setNotificationMessage({
        type: 'error',
        message: error?.response?.data?.message,
        code: error?.response?.data?.code,
        status: true,
      }),
    );
    throw error;
  }
});

export const fetchStationListFilter = createAsyncThunk<CommonFilterProperties[], void>(
  'account/fetchStationListFilter',
  async (data, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.stationOverridesFiltersList({ data });
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountListInitialProperties = {
  accountList: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const StationListPenalty = createSlice({
  name: 'account-penalty',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updateAccountListFilter: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * single Updated account list data
     */
    updatedSingleAccount: (
      state: AccountListInitialProperties,
      action: PayloadAction<AccountListInterface>,
    ) => {
      const updatedAccount = action.payload; // account to be added at the top
      const accountListData = state.accountList; // Assign the existing account to another variable

      // Find the index of the updated account by ID
      const index = accountListData.findIndex(
        (eachAccount) => eachAccount._id === updatedAccount._id,
      );
      // If the index found, replace the entire data
      if (index !== -1) {
        accountListData[index] = {
          ...updatedAccount,
        };
      }
      state.accountList = accountListData;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountListInitialProperties>) => {
    builder
      .addCase(fetchStationsList.pending, (state: AccountListInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchStationsList.fulfilled,
        (
          state: AccountListInitialProperties,
          action: PayloadAction<AccountListResponseInterFace>,
        ) => {
          state.loading = false;
          state.accountList = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchStationsList.rejected, (state: AccountListInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });

    builder
      .addCase(fetchStationListFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchStationListFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchStationListFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateAccountListFilter, setSortData, updatedSingleAccount } =
  StationListPenalty.actions;
// Export the reducer
export default StationListPenalty.reducer;
